// Client-side error reporting to Google Cloud Logging (UP-3052). The browser
// can't hold Logging credentials, so events are relayed through the backend's
// POST /api/client-logs (verified token -> identity is stamped server-side).
// Design constraints: NEVER throw into the caller, never report failures of the
// reporting itself (no loops), dedup repeats, and cap the per-session volume.

export interface ClientLogEvent {
  severity: "error" | "warn";
  message: string;
  name?: string;
  stack?: string;
  componentStack?: string;
  surface: string;
  route: string;
  appVersion?: string;
  occurredAt: string;
}

export type ClientLogTransport = (events: ClientLogEvent[]) => Promise<unknown>;

const PENDING_CAP = 20;
const SESSION_CAP = 100;
const DEDUP_WINDOW_MS = 30_000;

let transport: ClientLogTransport | null = null;
let pending: ClientLogEvent[] = [];
let sentCount = 0;
const recentlySent = new Map<string, number>();

function buildEvent(
  error: unknown,
  context: { surface: string; componentStack?: string; severity?: "error" | "warn" }
): ClientLogEvent {
  const err = error instanceof Error ? error : null;
  const message =
    err?.message ??
    (typeof error === "string" ? error : JSON.stringify(error ?? "(unknown)"));
  return {
    severity: context.severity ?? "error",
    message: String(message),
    name: err?.name,
    stack: err?.stack,
    componentStack: context.componentStack,
    surface: context.surface,
    route: window.location.hash || window.location.pathname,
    appVersion: process.env.REACT_APP_VERSION,
    occurredAt: new Date().toISOString(),
  };
}

function isDuplicate(event: ClientLogEvent): boolean {
  const key = `${event.surface}|${event.message}`;
  const lastAt = recentlySent.get(key);
  const now = Date.now();
  if (lastAt !== undefined && now - lastAt < DEDUP_WINDOW_MS) return true;
  recentlySent.set(key, now);
  return false;
}

function deliver(events: ClientLogEvent[]): void {
  if (!transport || events.length === 0) return;
  transport(events).catch(() => {
    // reporting must never surface or re-report its own failures
  });
}

export function logClientError(
  error: unknown,
  context: { surface: string; componentStack?: string; severity?: "error" | "warn" }
): void {
  try {
    if (sentCount >= SESSION_CAP) return;
    const event = buildEvent(error, context);
    if (isDuplicate(event)) return;
    sentCount += 1;
    if (transport) {
      deliver([event]);
    } else if (pending.length < PENDING_CAP) {
      pending.push(event);
    }
  } catch {
    // never let reporting break the app
  }
}

// AuthContext registers the authenticated sender once `api` exists; events
// raised before sign-in finished are buffered and flushed here.
export function setClientLogTransport(next: ClientLogTransport | null): void {
  transport = next;
  if (transport && pending.length > 0) {
    const buffered = pending;
    pending = [];
    deliver(buffered);
  }
}

// Uncaught errors never pass showError/ErrorBoundary — catch them at the window.
export function installGlobalErrorHandlers(): void {
  window.addEventListener("error", (event) => {
    logClientError(event.error ?? event.message, { surface: "window.onerror" });
  });
  window.addEventListener("unhandledrejection", (event) => {
    logClientError(event.reason, { surface: "unhandledrejection" });
  });
}

export function __resetClientLogger(): void {
  transport = null;
  pending = [];
  sentCount = 0;
  recentlySent.clear();
}
